import React, { useState } from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTransactionalOrders } from '@/context/TransactionalOrdersContext';
import { TransactionalOrderUI } from '@/api/TransactionalOrdersApi';
import { formatCurrency } from '@/utils/formatCurrency';
import { LinkTransactionDialog } from './LinkTransactionDialog';
import { CreateTransactionFromOrderDialog } from './CreateTransactionFromOrderDialog';
import { Link2, Unlink, PlusCircle, RefreshCw } from 'lucide-react';

export const BrokerTransactionsTable = (): React.ReactNode => {
  const { orders, loading, error, unlinkTransaction, refresh } = useTransactionalOrders();

  const [linkDialogOrder, setLinkDialogOrder] = useState<TransactionalOrderUI | null>(null);
  const [createDialogOrder, setCreateDialogOrder] = useState<TransactionalOrderUI | null>(null);
  const [unlinking, setUnlinking] = useState<number | null>(null);

  const handleUnlink = async (orderId: number) => {
    setUnlinking(orderId);
    try {
      await unlinkTransaction(orderId);
    } catch (err) {
      console.error('Failed to unlink:', err);
    } finally {
      setUnlinking(null);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="p-8">
          <div className="text-center">Loading broker transactions...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardContent className="p-8">
          <div className="text-center text-red-600">{error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="w-full">
        <CardHeader className="flex flex-row items-center">
          <div className="grid gap-2">
            <CardTitle className="text-2xl">Broker Transactions</CardTitle>
            <CardDescription>
              Non-trading transactions from your broker (deposits, withdrawals, balance adjustments). Link them to your fund transactions for accurate tracking.
            </CardDescription>
          </div>
          <Button onClick={() => refresh()} variant="outline" size="sm" className="ml-auto gap-1" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => {
                const dateObj = new Date(order.time * 1000);
                const day = dateObj.getDate().toString().padStart(2, '0');
                const month = dateObj.toLocaleString('en-US', { month: 'short' });
                const year = dateObj.getFullYear();
                const displayDate = `${day}-${month}-${year}`;

                const isLinked = order.linked_transaction_id !== null;
                const isDeposit = order.amount > 0;

                return (
                  <TableRow key={order.id}>
                    <TableCell>
                      <div className="font-medium">{displayDate}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium capitalize">{order.type}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`font-medium ${isDeposit ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(Math.abs(order.amount))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-muted-foreground">{order.description || '-'}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={isLinked ? 'default' : 'outline'}>
                        {isLinked ? 'Linked' : 'Unlinked'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {isLinked ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnlink(order.id)}
                          disabled={unlinking === order.id}
                        >
                          <Unlink className="h-4 w-4 mr-1" />
                          {unlinking === order.id ? 'Unlinking...' : 'Unlink'}
                        </Button>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setLinkDialogOrder(order)}
                          >
                            <Link2 className="h-4 w-4 mr-1" />
                            Link
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCreateDialogOrder(order)}
                          >
                            <PlusCircle className="h-4 w-4 mr-1" />
                            Create
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {orders.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No broker transactions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <LinkTransactionDialog
        order={linkDialogOrder}
        open={linkDialogOrder !== null}
        onClose={() => setLinkDialogOrder(null)}
      />

      <CreateTransactionFromOrderDialog
        order={createDialogOrder}
        open={createDialogOrder !== null}
        onClose={() => setCreateDialogOrder(null)}
      />
    </>
  );
};
